import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import api from '../../lib/api';

export type CustomerTier = {
  id: string;
  name: string;
  discountAmountCents: number;
  badgeColor: string;
  customerCount: number;
  createdAt: string;
  updatedAt: string;
};

export type AssignedCustomerTier = Pick<
  CustomerTier,
  'id' | 'name' | 'discountAmountCents' | 'badgeColor'
>;

export type CustomerTierPayload = {
  name: string;
  discountAmountCents: number;
  badgeColor: string;
};

const customerTierKeys = {
  root: ['customer-tiers'] as const,
};

function invalidateCustomerTierQueries(qc: ReturnType<typeof useQueryClient>, customerId: string) {
  void qc.invalidateQueries({
    queryKey: customerTierKeys.root,
  });

  void qc.invalidateQueries({
    queryKey: ['customers'],
  });

  void qc.invalidateQueries({
    queryKey: ['customers', 'detail', customerId],
  });

  void qc.invalidateQueries({
    queryKey: ['customers:get', customerId],
  });

  void qc.invalidateQueries({
    queryKey: ['customer-addresses', customerId],
  });

  void qc.invalidateQueries({
    queryKey: ['addresses', customerId],
  });

  void qc.invalidateQueries({
    queryKey: ['auditLogs'],
  });
}

export function useListCustomerTiers() {
  return useQuery({
    queryKey: customerTierKeys.root,

    queryFn: async () => {
      const { data } = await api.get<{
        tiers: CustomerTier[];
      }>('/organization/customer-tiers');

      return data;
    },
  });
}

export function useCreateCustomerTier() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CustomerTierPayload) => {
      const { data } = await api.post<{
        tier: CustomerTier;
      }>('/organization/customer-tiers', payload);

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: customerTierKeys.root,
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useUpdateCustomerTier() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      tierId,
      ...payload
    }: CustomerTierPayload & {
      tierId: string;
    }) => {
      const { data } = await api.patch<{
        tier: CustomerTier;
      }>(`/organization/customer-tiers/${tierId}`, payload);

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: customerTierKeys.root,
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useDeleteCustomerTier() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (tierId: string) => {
      const { data } = await api.delete<{
        deletedTierId: string;
        unassignedCustomerCount: number;
        disabledLocationCount: number;
      }>(`/organization/customer-tiers/${tierId}`);

      return data;
    },

    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: customerTierKeys.root,
      });

      void qc.invalidateQueries({
        queryKey: ['customers'],
      });

      void qc.invalidateQueries({
        queryKey: ['customer-addresses'],
        exact: false,
      });

      void qc.invalidateQueries({
        queryKey: ['addresses'],
        exact: false,
      });

      void qc.invalidateQueries({
        queryKey: ['auditLogs'],
      });
    },
  });
}

export function useSetCustomerLocationTierAccess() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({
      customerId: _customerId,
      addressId,
      tierId,
      enabled,
    }: {
      customerId: string;
      addressId: string;
      tierId: string;
      enabled: boolean;
    }) => {
      const { data } = await api.put<{
        customer: {
          id: string;
          tierId: string | null;
          tier: AssignedCustomerTier | null;
        };

        address: {
          id: string;
          customerId: string | null;
          applyCustomerTierDiscount: boolean;
        };
      }>(`/organization/customers/customer-locations/${addressId}/tier-access`, {
        tierId,
        enabled,
      });

      return data;
    },

    onSuccess: (_data, variables) => {
      invalidateCustomerTierQueries(qc, variables.customerId);
    },
  });
}

/**
 * Assigns the selected tier to Customer.tierId and enables
 * applyCustomerTierDiscount for every existing delivery
 * location belonging to the customer.
 */
export function useEnableCustomerTierForAllLocations() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId, tierId }: { customerId: string; tierId: string }) => {
      const { data } = await api.patch<{
        customer: {
          id: string;
          tierId: string | null;
          tier: AssignedCustomerTier | null;
        };

        tierLocationsUpdated: number;
      }>(`/organization/customers/${customerId}`, {
        tierId,
        applyTierToAllLocations: true,
      });

      return data;
    },

    onSuccess: (_data, variables) => {
      invalidateCustomerTierQueries(qc, variables.customerId);
    },
  });
}
